import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { RouterProvider } from "react-router-dom";
import { router } from "./routes/router";
import "./index.css";
import { PersistQueryClientProvider } from "@tanstack/react-query-persist-client";
import { persister, queryClient } from "./lib/query-client";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import * as Sentry from "@sentry/react";
import { enableMocking } from "./lib/test-utils";
import "./lib/i18next";
import { registerSW } from "virtual:pwa-register";

Sentry.init({
  enabled: import.meta.env.MODE === "production",
  dsn: "https://aaccafb3281209fd0d2583208aa6b11a@o4508496633856000.ingest.us.sentry.io/4508500101103616",
  integrations: [
    Sentry.browserTracingIntegration(),
    Sentry.replayIntegration({
      maskAllText: false,
    }),
  ],
  tracesSampleRate: 1.0,
  replaysSessionSampleRate: 0.1,
  replaysOnErrorSampleRate: 1.0,
});

// Register service worker with periodic update checks
const intervalMS = 60 * 1000; // Check for updates every 60 seconds

registerSW({
  onRegisteredSW(swUrl, registration) {
    if (registration) {
      setInterval(async () => {
        if (!(!registration.installing && navigator)) return;
        if ("connection" in navigator && !navigator.onLine) return;
        const resp = await fetch(swUrl, {
          cache: "no-store",
          headers: {
            cache: "no-store",
            "cache-control": "no-cache",
          },
        });
        if (resp?.status === 200) {
          await registration.update();
        }
      }, intervalMS);
    }
  },
  onNeedRefresh() {
    // With autoUpdate + skipWaiting, this auto-refreshes
  },
  onOfflineReady() {
    console.log("App ready to work offline");
  },
});

enableMocking().then(() => {
  createRoot(document.getElementById("root")!).render(
    <StrictMode>
      <PersistQueryClientProvider
        persistOptions={{ persister }}
        client={queryClient}
      >
        <RouterProvider router={router} />
        <ReactQueryDevtools initialIsOpen={false} />
      </PersistQueryClientProvider>
    </StrictMode>
  );
});
